import React from 'react';
import { CheckCircle2 } from 'lucide-react';

// Iter 111 · colour per retest tag type (long = greens, short = reds/oranges)
export const TAG_STYLES = {
  near_retest:        'border-amber-400/70 text-amber-300 bg-amber-400/10',
  shallow_long:       'border-emerald-400/70 text-emerald-300 bg-emerald-400/10',
  primary_long:       'border-green-500 text-green-300 bg-green-500/20',
  shallow_short:      'border-orange-500/80 text-orange-300 bg-orange-500/10',
  primary_short:      'border-red-500 text-red-300 bg-red-500/20',
  breakout_retest:    'border-sky-400/70 text-sky-300 bg-sky-400/10',
  breakdown_retest:   'border-fuchsia-500/70 text-fuchsia-300 bg-fuchsia-500/10',
  rejection_candle:   'border-teal-400/70 text-teal-200 bg-teal-400/10',
  retest_failed:      'border-zinc-500 text-zinc-400 bg-zinc-500/10 line-through decoration-zinc-500',
  retest_invalidated: 'border-zinc-600 text-zinc-500 bg-zinc-700/20',
  testing_level:      'border-sky-300/50 text-sky-200 bg-sky-300/5',
  momentum_conflict:  'border-yellow-500/80 text-yellow-300 bg-yellow-500/10 border-dashed',
  venue_disagree:     'border-violet-400/70 text-violet-300 bg-violet-400/10 border-dashed',
  unverified:         'border-zinc-400/60 text-zinc-300 bg-zinc-400/5 border-dashed',
};

export const STATE_STYLES = {
  CONFIRMED_LONG: 'text-green-300', CONFIRMED_SHORT: 'text-red-300',
  BREAKOUT_CONTINUATION: 'text-green-300', BREAKDOWN_CONTINUATION: 'text-red-300',
  BULLISH_REJECTION: 'text-lime-300', BEARISH_REJECTION: 'text-rose-300',
  LEVEL_TOUCHED: 'text-sky-300', RETEST_FORMING: 'text-sky-300',
  NEAR_RETEST: 'text-amber-300', APPROACHING: 'text-amber-200/70',
  FAILED: 'text-zinc-400', INVALIDATED: 'text-zinc-500', EXPIRED: 'text-zinc-600', NONE: 'text-zinc-600',
};

export function RetestTag({ tag, testId }) {
  const cls = TAG_STYLES[tag.key] || 'border-cyan-sys/60 text-cyan-sys';
  return (
    <span
      data-testid={testId}
      title={tag.confirmed ? `confirmed ${tag.direction.toLowerCase()} signal (analysis only)` : 'no directional call — see the expanded card'}
      className={`inline-flex items-center gap-1 text-[8px] tracking-widest uppercase px-1 py-0.5 border whitespace-nowrap ${cls}`}
    >
      {tag.confirmed && <CheckCircle2 size={9} />}
      {tag.label}
    </span>
  );
}

export const fmtPx = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const a = Math.abs(v);
  if (a >= 1000) return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (a >= 1) return Number(v).toFixed(4);
  return Number(v).toPrecision(5);
};

export const fmtPct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(2)}%`);

export const fmtTime = (ts) => {
  if (!ts) return '—';
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const NEUTRAL = {
  NONE: 'No setup', APPROACHING: 'Approaching level', NEAR_RETEST: 'Near retest point',
  LEVEL_TOUCHED: 'Testing level', RETEST_FORMING: 'Testing level',
  BULLISH_REJECTION: 'Rejection candle · momentum not aligned', BEARISH_REJECTION: 'Rejection candle · momentum not aligned',
  CONFIRMED_LONG: 'Structure confirmed · no call', CONFIRMED_SHORT: 'Structure confirmed · no call',
  BREAKOUT_CONTINUATION: 'Structure confirmed · no call', BREAKDOWN_CONTINUATION: 'Structure confirmed · no call',
  FAILED: 'Retest failed', INVALIDATED: 'Invalidated', EXPIRED: 'Expired',
};

// Iter 112 · direction words/colours only for an issued (actionable) signal
export function setupLabel(s) {
  if (!s) return { text: 'no setup', cls: 'text-[var(--txt-muted)]' };
  if (s.signal?.actionable) {
    return { text: `Confirmed · ${s.direction === 'LONG' ? 'Long' : 'Short'}`, cls: s.direction === 'LONG' ? 'text-green-300' : 'text-red-300' };
  }
  const tag = s.tags?.[0];
  if (tag) {
    const warn = ['momentum_conflict', 'venue_disagree', 'unverified'].includes(tag.key);
    return { text: tag.label.replace(/^\S+\s/, ''), cls: warn ? 'text-yellow-300' : 'text-sky-200' };
  }
  return { text: NEUTRAL[s.state] || s.state, cls: ['FAILED', 'INVALIDATED', 'EXPIRED'].includes(s.state) ? 'text-zinc-500' : 'text-sky-200/80' };
}
